import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Save, Send, Eye, Mail, Users, Loader2, CheckCircle2, X, Layers, AlertCircle, Bookmark } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { useApp } from '../../context/AppContext';
import {
  getCampaign, updateCampaign, getCampaignTemplates, renderTemplateBody, renderBlock,
  getSubscriberFilters, getCampaignAudience, sendTestEmail, sendCampaign,
  listSenderProfiles, getAudienceBreakdown, saveCampaignTemplate,
} from '../../lib/campaignsApi';
import { getRecipientPreview, confirmRecipientPreview, getCustomerEnums, getProspectCampaignFilterOptions } from '../../lib/api';
import { RichTextEditor } from '../../components/RichTextEditor';
import RecipientPreviewModal from './RecipientPreviewModal';

const BlockButton = ({ block, onPick, testid }) => (
  <button
    type="button"
    onClick={() => onPick(block.key)}
    className="text-left border border-white/10 bg-[#050505] hover:border-[#00D4FF]/40 transition-colors p-3"
    data-testid={testid}
  >
    <div className="flex items-center gap-2 mb-1">
      <span className="w-2 h-2" style={{ background: block.color }} />
      <div className="text-white text-sm font-medium">{block.name}</div>
    </div>
    <div className="text-[11px] text-white/40">
      {block.hint || 'Insert block'}
    </div>
  </button>
);

// Feb-2026 — Localized labels + short hints for the content-block
// cards. Keys match the backend `list_blocks()` response so lookup is
// trivial. Labels intentionally read as verbs / plain names — the
// underlying block keys, URLs, templates, and tracking are unchanged.
const BLOCK_LABELS = {
  en: {
    featured_inventory: 'Featured Inventory',
    bulk_lot: 'Wholesale Lot',
    new_arrival: 'New Arrivals',
    custom_sourcing: 'Custom Sourcing',
    special_promotion: 'Special Offer',
    hero: 'Main Header',
    product_spotlight: 'Product Spotlight',
    custom_cta: 'Action Button',
    divider: 'Divider',
  },
  es: {
    featured_inventory: 'Inventario Destacado',
    bulk_lot: 'Lote Mayorista',
    new_arrival: 'Nuevas Llegadas',
    custom_sourcing: 'Búsqueda Personalizada',
    special_promotion: 'Oferta Especial',
    hero: 'Encabezado Principal',
    product_spotlight: 'Producto Destacado',
    custom_cta: 'Botón de Acción',
    divider: 'Separador',
  },
};

const BLOCK_HINTS = {
  en: {
    featured_inventory: 'Add selected inventory',
    bulk_lot: 'Promote a bulk lot',
    new_arrival: 'Highlight new inventory',
    custom_sourcing: 'Invite sourcing requests',
    special_promotion: 'Add a limited offer',
    hero: 'Add the campaign introduction',
    product_spotlight: 'Feature one product',
    custom_cta: 'Add a clickable button',
    divider: 'Add spacing between sections',
  },
  es: {
    featured_inventory: 'Agregar inventario seleccionado',
    bulk_lot: 'Promocionar un lote',
    new_arrival: 'Destacar inventario nuevo',
    custom_sourcing: 'Invitar solicitudes de búsqueda',
    special_promotion: 'Agregar una oferta especial',
    hero: 'Agregar la introducción',
    product_spotlight: 'Destacar un producto',
    custom_cta: 'Agregar un botón',
    divider: 'Separar las secciones',
  },
};

const PANEL_STRINGS = {
  en: {
    title: 'Add to Email',
    subtitle: 'Click to insert at the cursor position.',
  },
  es: {
    title: 'Agregar al correo',
    subtitle: 'Haz clic para insertar en la posición del cursor.',
  },
};

// Feb-2026 — Visual Blocks that require a per-insert configuration
// dialog. Other block keys keep the legacy click-to-insert behavior.
const CONFIGURABLE_BLOCKS = new Set(['hero', 'product_spotlight', 'custom_cta', 'divider']);

// Default params for the config dialog per block, per language. Kept
// deliberately concise so admins usually only edit 1–2 fields.
const BLOCK_DEFAULTS = {
  en: {
    hero: { headline: "This Week's Wholesale Highlights", description: 'Hand-picked refurbished laptops, ready to ship from Miami.', image_url: '', cta_text: 'View Inventory', cta_url: 'https://luxortek.com/products' },
    product_spotlight: { image_url: '', model: '', product_id: '', specs: '', grade: 'A', quantity: '', price: '', cta_text: 'View Details', cta_url: 'https://luxortek.com/products' },
    custom_cta: { cta_text: 'Learn More', cta_url: 'https://luxortek.com/products', color: 'cyan', align: 'left' },
    divider: { style: 'divider' },
  },
  es: {
    hero: { headline: 'Destacados Mayoristas de la Semana', description: 'Laptops refabricados seleccionados a mano, listos para exportar desde Miami.', image_url: '', cta_text: 'Ver Inventario', cta_url: 'https://luxortek.com/products' },
    product_spotlight: { image_url: '', model: '', product_id: '', specs: '', grade: 'A', quantity: '', price: '', cta_text: 'Ver Detalles', cta_url: 'https://luxortek.com/products' },
    custom_cta: { cta_text: 'Más Información', cta_url: 'https://luxortek.com/products', color: 'cyan', align: 'left' },
    divider: { style: 'divider' },
  },
};

const Chip = ({ children, active, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-3 py-1 text-xs border transition-colors ${
      active
        ? 'bg-[#00D4FF] text-black border-[#00D4FF]'
        : 'border-white/15 text-white/70 hover:border-white/30 hover:text-white'
    }`}
    data-testid={testid}
  >
    {children}
  </button>
);

// Feb-2026 — Prospect qualification enums. Kept in sync with the
// server-side sets in `backend/routes/prospects.py` (CAMPAIGN_AUDIENCES /
// CAMPAIGN_ELIGIBILITIES / QUALIFICATION_CONFIDENCES). Order matters
// only for UI presentation.
const CAMPAIGN_AUDIENCE_OPTIONS = [
  'Channel Buyer',
  'Integrator / MSP',
  'Corporate / Institutional',
  'Other / Unclassified',
];
const CAMPAIGN_ELIGIBILITY_OPTIONS = [
  'Ready for New Campaign',
  'Follow-Up Only',
  'Direct Personal Follow-Up',
  'Manual Review',
  'Exclude',
];
const QUALIFICATION_CONFIDENCE_OPTIONS = ['High', 'Medium', 'Low'];

export const AdminCampaignEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { adminInfo } = useApp();

  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [preheader, setPreheader] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [audience, setAudience] = useState('all');           // 'all' | 'filtered'
  const [audienceType, setAudienceType] = useState('subscribers');  // subscribers|leads|prospects|buyers|all
  const [breakdown, setBreakdown] = useState(null);  // {subscribers,leads,prospects,buyers,total_raw,deduplicated_recipients,unsubscribed_excluded}
  const [countries, setCountries] = useState([]);            // selected
  const [segments, setSegments] = useState([]);              // selected
  const [territory, setTerritory] = useState('');            // '' | Mexico | Central America | South America | Caribbean | Multi-Country
  // Feb-18 Subscriber Joined Date filter (applies only when
  // audience_type='subscribers' or 'all'). `operator` empty = inactive.
  // Date strings are 'YYYY-MM-DD' (passed straight to <input type=date>).
  const [joinedDate, setJoinedDate] = useState({ operator: '', value: '', end_value: '' });
  // Phase 3 — master Customers / Suppliers filter state. Empty default
  // values mean "no filter" so an empty submit returns the whole set.
  const [customerFilters, setCustomerFilters] = useState({
    language: '', stage: '', source: '', segment: '', import_batch: '',
    country: '', email_opt_in: '', search: '',
    last_contact: { operator: '', value: '', end_value: '' },
    last_campaign_sent: { operator: '', value: '', end_value: '' },
  });
  const [supplierFilters, setSupplierFilters] = useState({
    country: '', category: '', ranking: '', supplier_tier: '', search: '',
    last_contact: { operator: '', value: '', end_value: '' },
  });
  // Feb-2026 — Prospect master audience filters + batch-send controls.
  // `recipientLimit` empty = no cap; `skipAlreadyContacted` default
  // true so the same prospects never get re-contacted across batches.
  // Feb-2026 (Aug patch) — Qualification multi-selects were added but
  // never wired into the editor UI. For a NEW campaign the audience
  // list starts empty (must be deliberately selected), eligibility
  // defaults to "Ready for New Campaign", confidence stays optional.
  // Saved drafts overwrite these defaults when the campaign loads.
  const [prospectFilters, setProspectFilters] = useState({
    countries: [], source: '', import_batch: '',
    outreach_status: '', buyer_potential_tier: '', search: '',
    campaign_audiences: [],
    campaign_eligibilities: ['Ready for New Campaign'],
    qualification_confidences: [],
    // Feb-2026 — Language + Market persistent filters.
    languages: [],
    markets: [],
  });
  const [recipientLimit, setRecipientLimit] = useState('');
  const [skipAlreadyContacted, setSkipAlreadyContacted] = useState(true);
  // Advanced Filters collapsible — Country / Source / Import Batch /
  // Qualification Confidence are demoted here so the main panel stays
  // focused on audience-building essentials.
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const [prospectOptions, setProspectOptions] = useState({
    countries: [], sources: [], import_batches: [],
    outreach_statuses: [], buyer_potential_tiers: [],
    languages: ['English', 'Spanish', 'Portuguese', 'Unknown'],
    markets: ['USA/Canada', 'Mexico', 'Central America', 'South America',
              'Caribbean', 'Europe', 'Other'],
    total_prospects: 0,
  });
  // Customer enums (stages/sources/languages) — loaded once for dropdown options.
  const [custEnums, setCustEnums] = useState({ stages: [], sources: [], languages: [] });
  // Recipient Preview modal state (Phase 3 mandatory gate before Send).
  const [previewOpenP3, setPreviewOpenP3] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  // The hash returned by the last successful confirm. Send is gated on
  // this being non-empty AND matching the current filter hash.
  const [previewConfirmedHash, setPreviewConfirmedHash] = useState('');
  const [allCountries, setAllCountries] = useState([]);
  const [allSegments, setAllSegments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [blocks, setBlocks] = useState([]);
  // Feb-2026 — Content Blocks language selector. Controls the
  // language of NEWLY inserted blocks only. Does NOT re-render or
  // touch content already in the email body.
  const [blockLang, setBlockLang] = useState('en');
  // Feb-2026 — Visual Blocks config dialog. When admin clicks Hero /
  // Product Spotlight / Custom CTA / Divider we open a compact dialog
  // to collect per-insert params and then render the block via the
  // existing renderBlock() API. Non-visual blocks bypass this and go
  // straight to insertBlock() as before.
  const [blockConfigOpen, setBlockConfigOpen] = useState(false);
  const [blockConfigKey, setBlockConfigKey] = useState('');
  const [blockConfigParams, setBlockConfigParams] = useState({});
  const [audienceSummary, setAudienceSummary] = useState({ count: 0, by_country: [], by_segment: [] });
  const [senderProfiles, setSenderProfiles] = useState([]);
  const [senderProfileId, setSenderProfileId] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [testEmailTo, setTestEmailTo] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [testResult, setTestResult] = useState(null); // {status,to,message_id,error,sent_at,profile_email}
  const [readOnly, setReadOnly] = useState(false);
  const editorInsertRef = useRef(null);

  // ─── Save-as-Template modal (Phase 1 Campaign Templates) ──────────
  const TEMPLATE_CATEGORIES = ['First Contact', 'Weekly Inventory', 'Bulk Lots', 'New Arrivals', 'Follow Up', 'RFQ', 'Custom'];
  const [saveTplOpen, setSaveTplOpen] = useState(false);
  const [saveTplName, setSaveTplName] = useState('');
  const [saveTplCategory, setSaveTplCategory] = useState('Custom');
  const [savingTpl, setSavingTpl] = useState(false);

  const openSaveTemplate = () => {
    if (readOnly) return;
    if (!bodyHtml || !bodyHtml.trim()) {
      toast.error('Add some email content before saving as a template.');
      return;
    }
    setSaveTplName(name ? `${name} (template)` : '');
    setSaveTplCategory('Custom');
    setSaveTplOpen(true);
  };

  const submitSaveTemplate = async () => {
    if (!saveTplName.trim()) { toast.error('Name is required'); return; }
    try {
      setSavingTpl(true);
      await saveCampaignTemplate({
        name: saveTplName.trim(),
        category: saveTplCategory,
        subject: subject || '',
        preheader: preheader || '',
        html_body: bodyHtml || '',
      });
      toast.success(`Saved to ${saveTplCategory} templates`);
      setSaveTplOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save template');
    } finally {
      setSavingTpl(false);
    }
  };

  // ─── Load campaign + meta ─────────────────────────────────────────
  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [c, t, f] = await Promise.all([
        getCampaign(id),
        getCampaignTemplates(),
        getSubscriberFilters(),
      ]);
      // Sender profiles load independently — don't block editor on it
      let sp = [];
      try {
        const spRes = await listSenderProfiles();
        sp = spRes.data || [];
      } catch (e) { /* non-fatal */ }
      setCampaign(c.data);
      setName(c.data.name || '');
      setSubject(c.data.subject || '');
      setPreheader(c.data.preheader || '');
      setBodyHtml(c.data.body_html || '');
      const f0 = c.data.filters || {};
      setAudience(f0.audience || (f0.countries?.length || f0.segments?.length || f0.joined_date?.operator ? 'filtered' : 'all'));
      setAudienceType((f0.audience_type || 'subscribers').toLowerCase());
      setCountries(f0.countries || []);
      setSegments(f0.segments || []);
      setTerritory(f0.territory || '');
      setJoinedDate({
        operator: (f0.joined_date && f0.joined_date.operator) || '',
        value: (f0.joined_date && f0.joined_date.value) || '',
        end_value: (f0.joined_date && f0.joined_date.end_value) || '',
      });
      // Phase-3 filter restoration
      if (f0.customer_filters) {
        setCustomerFilters({
          language: f0.customer_filters.language || '',
          stage: f0.customer_filters.stage || '',
          source: f0.customer_filters.source || '',
          segment: f0.customer_filters.segment || '',
          import_batch: f0.customer_filters.import_batch || '',
          country: f0.customer_filters.country || '',
          email_opt_in: f0.customer_filters.email_opt_in === true ? 'true'
                         : f0.customer_filters.email_opt_in === false ? 'false' : '',
          search: f0.customer_filters.search || '',
          last_contact: f0.customer_filters.last_contact || { operator: '', value: '', end_value: '' },
          last_campaign_sent: f0.customer_filters.last_campaign_sent || { operator: '', value: '', end_value: '' },
        });
      }
      if (f0.supplier_filters) {
        setSupplierFilters({
          country: f0.supplier_filters.country || '',
          category: f0.supplier_filters.category || '',
          ranking: f0.supplier_filters.ranking || '',
          supplier_tier: f0.supplier_filters.supplier_tier || '',
          search: f0.supplier_filters.search || '',
          last_contact: f0.supplier_filters.last_contact || { operator: '', value: '', end_value: '' },
        });
      }
      if (f0.prospect_filters) {
        // Feb-2026 multi-country: prefer the new `countries` array; fall
        // back to the legacy `country` scalar so campaigns / drafts /
        // templates saved before the multi-select rollout still load
        // with the correct country pre-selected.
        const savedCountries = Array.isArray(f0.prospect_filters.countries)
          ? f0.prospect_filters.countries.filter(Boolean)
          : [];
        const legacyCountry = (f0.prospect_filters.country || '').trim();
        const initialCountries = savedCountries.length
          ? savedCountries
          : (legacyCountry ? [legacyCountry] : []);

        const savedAuds = Array.isArray(f0.prospect_filters.campaign_audiences)
          ? f0.prospect_filters.campaign_audiences.filter(Boolean)
          : (f0.prospect_filters.campaign_audience ? [f0.prospect_filters.campaign_audience] : []);
        const savedEligs = Array.isArray(f0.prospect_filters.campaign_eligibilities)
          ? f0.prospect_filters.campaign_eligibilities.filter(Boolean)
          : (f0.prospect_filters.campaign_eligibility ? [f0.prospect_filters.campaign_eligibility] : []);
        const savedConfs = Array.isArray(f0.prospect_filters.qualification_confidences)
          ? f0.prospect_filters.qualification_confidences.filter(Boolean)
          : (f0.prospect_filters.qualification_confidence ? [f0.prospect_filters.qualification_confidence] : []);
        // Detect a brand-new / untouched Prospect campaign: no
        // qualification value has been persisted yet. In that case
        // apply the default (Eligibility = "Ready for New Campaign")
        // so the operator only has to deliberately pick an audience.
        // Any saved value (even a single scalar) means the operator
        // has interacted with this filter previously — keep whatever
        // was saved, including deliberately-empty lists.
        const isPristine = savedAuds.length === 0 && savedEligs.length === 0 && savedConfs.length === 0;
        setProspectFilters({
          countries: initialCountries,
          source: f0.prospect_filters.source || '',
          import_batch: f0.prospect_filters.import_batch || '',
          outreach_status: f0.prospect_filters.outreach_status || '',
          buyer_potential_tier: f0.prospect_filters.buyer_potential_tier || '',
          search: f0.prospect_filters.search || '',
          campaign_audiences: savedAuds,
          campaign_eligibilities: isPristine ? ['Ready for New Campaign'] : savedEligs,
          qualification_confidences: savedConfs,
          languages: Array.isArray(f0.prospect_filters.languages)
            ? f0.prospect_filters.languages.filter(Boolean) : [],
          markets: Array.isArray(f0.prospect_filters.markets)
            ? f0.prospect_filters.markets.filter(Boolean) : [],
        });
      }
      if (f0.recipient_limit != null) setRecipientLimit(String(f0.recipient_limit));
      if (typeof f0.skip_already_contacted === 'boolean') setSkipAlreadyContacted(f0.skip_already_contacted);
      setPreviewConfirmedHash(c.data.preview_confirmed_hash || '');
      setReadOnly(!(c.data.status === 'draft' || c.data.status === 'failed'));
      setTemplates(t.data.templates || []);
      setBlocks(t.data.blocks || []);
      setAllCountries(f.data.countries || []);
      setAllSegments(f.data.segments || []);
      setSenderProfiles(sp);
      // Default selected profile: campaign's own, else system default
      const initial = c.data.sender_profile_id || (sp.find((p) => p.is_default) || {}).id || '';
      setSenderProfileId(initial);
      setTestEmailTo(adminInfo?.email || 'admin@luxortek.com');
    } catch (e) {
      toast.error('Failed to load campaign');
    } finally {
      setLoading(false);
    }
  }, [id, adminInfo]);

  useEffect(() => { load(); }, [load]);

  // Phase 3 — fetch enums for the Customer filter dropdowns once.
  useEffect(() => {
    getCustomerEnums().then(r => setCustEnums(r.data)).catch(() => {});
    // Feb-2026 — fetch live prospect filter enums (Apollo source +
    // import_batch labels + statuses + countries).
    getProspectCampaignFilterOptions().then(r => setProspectOptions(r.data)).catch(() => {});
  }, []);

  // ── Phase 3 — Recipient Preview handlers ─────────────────────────
  // Translate the customer-filters state into the wire-format expected
  // by the backend (empty strings stripped, opt_in coerced to bool).
  const buildCustomerFiltersPayload = useCallback(() => {
    const cf = customerFilters;
    const out = {};
    if (cf.language) out.language = cf.language;
    if (cf.stage) out.stage = cf.stage;
    if (cf.source) out.source = cf.source;
    if (cf.segment) out.segment = cf.segment;
    if (cf.import_batch) out.import_batch = cf.import_batch;
    if (cf.country) out.country = cf.country;
    if (cf.search) out.search = cf.search;
    if (cf.email_opt_in === 'true') out.email_opt_in = true;
    if (cf.email_opt_in === 'false') out.email_opt_in = false;
    if (cf.last_contact?.operator) out.last_contact = cf.last_contact;
    if (cf.last_campaign_sent?.operator) out.last_campaign_sent = cf.last_campaign_sent;
    return out;
  }, [customerFilters]);

  const buildSupplierFiltersPayload = useCallback(() => {
    const sf = supplierFilters;
    const out = {};
    if (sf.country) out.country = sf.country;
    if (sf.category) out.category = sf.category;
    if (sf.ranking) out.ranking = sf.ranking;
    if (sf.supplier_tier) out.supplier_tier = sf.supplier_tier;
    if (sf.search) out.search = sf.search;
    if (sf.last_contact?.operator) out.last_contact = sf.last_contact;
    return out;
  }, [supplierFilters]);

  const buildProspectFiltersPayload = useCallback(() => {
    const pf = prospectFilters;
    const out = {};
    // Feb-2026 multi-country: canonical field is `countries` (array).
    // Also emit the legacy `country` scalar so any downstream consumer
    // still reading the single string keeps working (single = first;
    // multi = "" so it doesn't accidentally narrow the query).
    const countries = (pf.countries || []).map((c) => (c || '').trim()).filter(Boolean);
    if (countries.length > 0) {
      out.countries = countries;
      out.country = countries.length === 1 ? countries[0] : '';
    }
    if (pf.source) out.source = pf.source;
    if (pf.import_batch) out.import_batch = pf.import_batch;
    if (pf.outreach_status) out.outreach_status = pf.outreach_status;
    if (pf.buyer_potential_tier) out.buyer_potential_tier = pf.buyer_potential_tier;
    if (pf.search) out.search = pf.search;
    // Feb-2026 qualification multi-selects. Same shape as `countries`:
    // emit the canonical multi-value array plus the legacy scalar so
    // any downstream reader still keys off the singular field.
    const auds = (pf.campaign_audiences || []).map((v) => (v || '').trim()).filter(Boolean);
    if (auds.length > 0) {
      out.campaign_audiences = auds;
      out.campaign_audience = auds.length === 1 ? auds[0] : '';
    }
    const eligs = (pf.campaign_eligibilities || []).map((v) => (v || '').trim()).filter(Boolean);
    if (eligs.length > 0) {
      out.campaign_eligibilities = eligs;
      out.campaign_eligibility = eligs.length === 1 ? eligs[0] : '';
    }
    const confs = (pf.qualification_confidences || []).map((v) => (v || '').trim()).filter(Boolean);
    if (confs.length > 0) {
      out.qualification_confidences = confs;
      out.qualification_confidence = confs.length === 1 ? confs[0] : '';
    }
    // Feb-2026 — Language + Market filters. Both are OR-matched by the
    // backend. "Any" is represented by an empty array.
    const langs = (pf.languages || []).map((v) => (v || '').trim()).filter(Boolean);
    if (langs.length > 0) out.languages = langs;
    const mkts = (pf.markets || []).map((v) => (v || '').trim()).filter(Boolean);
    if (mkts.length > 0) out.markets = mkts;
    return out;
  }, [prospectFilters]);

  const openRecipientPreview = useCallback(async () => {
    setPreviewOpenP3(true);
    setPreviewLoading(true);
    try {
      const cf = audienceType === 'customers' ? buildCustomerFiltersPayload() : null;
      const sf = audienceType === 'suppliers' ? buildSupplierFiltersPayload() : null;
      const pf = audienceType === 'prospects' ? buildProspectFiltersPayload() : null;
      const rl = recipientLimit ? Number(recipientLimit) : null;
      const r = await getRecipientPreview(id, audienceType, cf, sf, pf, rl, skipAlreadyContacted);
      setPreviewData(r.data);
      if (previewConfirmedHash && previewConfirmedHash !== r.data.filters_hash) {
        setPreviewConfirmedHash('');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to compute preview');
    } finally {
      setPreviewLoading(false);
    }
  }, [audienceType, buildCustomerFiltersPayload, buildSupplierFiltersPayload, buildProspectFiltersPayload, id, previewConfirmedHash, recipientLimit, skipAlreadyContacted]);

  const confirmPreview = useCallback(async (hash) => {
    try {
      const r = await confirmRecipientPreview(id, audienceType, hash);
      setPreviewConfirmedHash(r.data.confirmed_hash);
      toast.success(`Confirmed ${previewData?.counts?.final ?? 0} recipients. Send is now enabled.`);
      setPreviewOpenP3(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to confirm preview');
    }
  }, [audienceType, id, previewData]);

  // Phase-3 + Feb-2026 audiences require explicit preview confirmation
  // before the Send button is enabled. Legacy audiences bypass this gate.
  const sendGateBlocked =
    (audienceType === 'customers' || audienceType === 'suppliers' || audienceType === 'prospects') &&
    (!previewData || !previewConfirmedHash || previewConfirmedHash !== previewData.filters_hash);

  // ─── Live audience count + breakdown ──────────────────────────────
  const refreshAudience = useCallback(async () => {
    try {
      const r = await getCampaignAudience(id);
      setAudienceSummary(r.data);
    } catch (e) {
      // ignore
    }
  }, [id]);

  const refreshBreakdown = useCallback(async (typeOverride, joinedOverride) => {
    try {
      const r = await getAudienceBreakdown({
        audience_type: typeOverride || audienceType,
        countries: audience === 'filtered' ? countries : [],
        segments: audience === 'filtered' ? segments : [],
        territory: territory || '',
        joined_date: (audience === 'filtered' && (joinedOverride || joinedDate).operator)
          ? (joinedOverride || joinedDate)
          : null,
      });
      setBreakdown(r.data);
    } catch (e) {
      setBreakdown(null);
    }
  }, [audienceType, audience, countries, segments, territory, joinedDate]);

  // Persist filter changes server-side then refresh count + breakdown.
  // `next` may omit `joined_date` — we merge in the current joinedDate
  // state so each callsite doesn't have to repeat it.
  const syncFilters = async (next) => {
    try {
      const merged = {
        joined_date: (next.audience === 'filtered' && joinedDate.operator) ? joinedDate : { operator: '', value: '', end_value: '' },
        ...next,
      };
      await updateCampaign(id, { filters: merged });
      await refreshAudience();
      await refreshBreakdown(merged.audience_type);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { if (!loading) { refreshAudience(); refreshBreakdown(); } }, [loading, refreshAudience, refreshBreakdown]);

  // ─── Save draft ───────────────────────────────────────────────────
  const handleSave = async (silent = false) => {
    if (readOnly) return;
    try {
      setSaving(true);
      const filters = {
        audience,
        audience_type: audienceType,
        countries: audience === 'filtered' ? countries : [],
        segments: audience === 'filtered' ? segments : [],
        territory: territory || '',
        joined_date: (audience === 'filtered' && joinedDate.operator) ? joinedDate : { operator: '', value: '', end_value: '' },
      };
      // Preserve Phase-3 / Feb-2026 master-audience fields so saving
      // a draft doesn't blow away the recipient-preview hash.
      // Critical: without these, `await handleSave(true)` inside
      // handleSendNow would drop recipient_limit + skip_already_contacted,
      // recomputing the server-side hash and tripping the "Filters changed
      // since last preview" gate at /send time.
      if (audienceType === 'customers') {
        filters.customer_filters = buildCustomerFiltersPayload();
      } else if (audienceType === 'suppliers') {
        filters.supplier_filters = buildSupplierFiltersPayload();
      } else if (audienceType === 'prospects') {
        filters.prospect_filters = buildProspectFiltersPayload();
        filters.recipient_limit = recipientLimit ? Number(recipientLimit) : null;
        filters.skip_already_contacted = skipAlreadyContacted;
      }
      await updateCampaign(id, { name, subject, preheader, body_html: bodyHtml, sender_profile_id: senderProfileId || null, filters });
      if (!silent) toast.success('Draft saved');
    } catch (e) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // ─── Apply template ───────────────────────────────────────────────
  const applyTemplate = async (key) => {
    if (readOnly) return;
    if (bodyHtml && !window.confirm('Replace current email content with this template?')) return;
    try {
      const r = await renderTemplateBody(key);
      setSubject(r.data.subject || '');
      setPreheader(r.data.preheader || '');
      setBodyHtml(r.data.body_html || '');
      toast.success('Template applied');
    } catch (e) {
      toast.error('Failed to load template');
    }
  };

  // ─── Insert block at cursor ───────────────────────────────────────
  // Visual blocks (hero / product_spotlight / custom_cta / divider)
  // open a compact configuration dialog first; legacy blocks insert
  // immediately with their EN/ES defaults.
  const insertBlock = async (key) => {
    if (readOnly) return;
    if (CONFIGURABLE_BLOCKS.has(key)) {
      const defaults = (BLOCK_DEFAULTS[blockLang] || BLOCK_DEFAULTS.en)[key] || {};
      setBlockConfigKey(key);
      setBlockConfigParams({ ...defaults });
      setBlockConfigOpen(true);
      return;
    }
    try {
      // Pass the currently selected block language so the backend
      // substitutes Spanish (or English) defaults for the block's
      // heading, description and CTA text before rendering.
      const r = await renderBlock(key, { lang: blockLang });
      insertRenderedBlock(r.data.html);
    } catch (e) {
      toast.error('Failed to insert block');
    }
  };

  const insertRenderedBlock = (html) => {
    if (RichTextEditor.insertHtmlAtCursor) {
      RichTextEditor.insertHtmlAtCursor(html);
      const area = document.querySelector('[data-testid="campaign-editor-rte-area"]');
      if (area) setBodyHtml(area.innerHTML);
    } else {
      setBodyHtml((prev) => prev + html);
    }
    toast.success('Block inserted');
  };

  const submitBlockConfig = async () => {
    try {
      const params = { ...blockConfigParams, lang: blockLang };
      const r = await renderBlock(blockConfigKey, params);
      insertRenderedBlock(r.data.html);
      setBlockConfigOpen(false);
    } catch (e) {
      toast.error('Failed to insert block');
    }
  };

  // ─── Preview ──────────────────────────────────────────────────────
  const openPreview = async () => {
    try {
      await handleSave(true);
      // Use direct fetch w/ auth header to receive HTML
      const adminToken = localStorage.getItem('luxortek_admin_token');
      const resp = await fetch(`/api/admin/campaigns/${id}/preview`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      const html = await resp.text();
      setPreviewHtml(html);
      setPreviewOpen(true);
    } catch (e) {
      toast.error('Preview failed');
    }
  };

  // ─── Test send ────────────────────────────────────────────────────
  const handleTestSend = async () => {
    if (!testEmailTo) return toast.error('Enter a test email');
    try {
      setSendingTest(true);
      setTestResult(null);
      await handleSave(true);
      const r = await sendTestEmail(id, testEmailTo);
      // D1.8 — backend ALWAYS returns HTTP 200 now. Dispatch on the
      // payload's `success` field. We never raise 5xx because
      // Cloudflare replaces 5xx bodies with its own error page,
      // destroying the structured detail.
      const payload = r.data || {};
      const succeeded = payload.success === true && (payload.status === 'sent' || payload.status === 'queued');
      setTestResult({
        status: payload.status || (succeeded ? 'sent' : 'failed'),
        to: payload.to || testEmailTo,
        message_id: payload.message_id,
        sent_at: payload.sent_at,
        profile_email: payload.profile_email,
        error: payload.error,
      });
      if (succeeded) {
        toast.success(`Delivered to ${payload.to || testEmailTo}${payload.message_id ? ` · ${String(payload.message_id).slice(0, 20)}…` : ''}`);
      } else {
        toast.error(`Test failed: ${payload.error || 'Unknown SMTP error'}`);
      }
    } catch (e) {
      // Network/transport error path (genuine 5xx from infra, timeouts,
      // etc.). Surface whatever detail we can extract.
      const det = e?.response?.data?.detail;
      const errMsg = typeof det === 'string'
        ? det
        : (det?.error || det?.message || e?.message || 'Test send failed (network error)');
      setTestResult({
        status: 'failed',
        to: typeof det === 'object' ? det?.to : testEmailTo,
        error: errMsg,
        profile_email: typeof det === 'object' ? det?.profile_email : null,
      });
      toast.error(`Test failed: ${errMsg}`);
    } finally {
      setSendingTest(false);
    }
  };

  // ─── Live send ────────────────────────────────────────────────────
  const handleSendNow = async () => {
    if (!subject || !bodyHtml) return toast.error('Subject and body are required');
    // Prospect campaigns must have at least one Campaign Audience AND
    // one Campaign Eligibility selected — the qualification safety
    // filter has no meaningful default answer for either.
    if (audienceType === 'prospects') {
      if ((prospectFilters.campaign_audiences || []).length === 0) {
        return toast.error('Select at least one Campaign Audience before sending');
      }
      if ((prospectFilters.campaign_eligibilities || []).length === 0) {
        return toast.error('Select at least one Campaign Eligibility before sending');
      }
    }
    // Master audiences (customers / suppliers / prospects) use the
    // confirmed recipient-preview count; legacy audiences use the
    // older audienceBreakdown total.
    const isMaster = audienceType === 'customers' || audienceType === 'suppliers' || audienceType === 'prospects';
    const sendCount = isMaster
      ? (previewData?.counts?.final ?? 0)
      : (breakdown?.deduplicated_recipients ?? audienceSummary.count);
    if (sendCount === 0) return toast.error('No recipients match your audience filters');
    const audienceLabel = isMaster
      ? (audienceType === 'customers' ? 'customers' : audienceType === 'suppliers' ? 'suppliers' : 'prospects')
      : 'recipients';
    const ok = window.confirm(
      `Send "${subject}" now to ${sendCount} ${audienceLabel}? This cannot be undone.`
    );
    if (!ok) return;
    try {
      setSending(true);
      await handleSave(true);
      const r = await sendCampaign(id);
      toast.success(`Campaign queued — sending to ${r.data.queued_recipients} recipients`);
      navigate(`/admin/campaigns/${id}/detail`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  // ─── Country / Segment / Territory toggles ────────────────────────
  const toggleCountry = (c) => {
    const next = countries.includes(c) ? countries.filter((x) => x !== c) : [...countries, c];
    setCountries(next);
    syncFilters({ audience: 'filtered', audience_type: audienceType, countries: next, segments, territory });
  };
  const toggleSegment = (s) => {
    const next = segments.includes(s) ? segments.filter((x) => x !== s) : [...segments, s];
    setSegments(next);
    syncFilters({ audience: 'filtered', audience_type: audienceType, countries, segments: next, territory });
  };
  const setAudienceMode = (mode) => {
    setAudience(mode);
    if (mode === 'all') {
      setCountries([]); setSegments([]);
      setJoinedDate({ operator: '', value: '', end_value: '' });
      syncFilters({ audience: 'all', audience_type: audienceType, countries: [], segments: [], territory });
    } else {
      syncFilters({ audience: 'filtered', audience_type: audienceType, countries, segments, territory });
    }
  };
  const changeTerritory = (t) => {
    setTerritory(t);
    syncFilters({
      audience, audience_type: audienceType,
      countries: audience === 'filtered' ? countries : [],
      segments: audience === 'filtered' ? segments : [],
      territory: t,
    });
  };

  // Joined Date filter handler — applies to the subscribers slice.
  // Clearing the operator wipes the date values too so the persisted
  // filter doesn't carry stale dates.
  const changeJoinedDate = (next) => {
    const cleared = !next.operator;
    const merged = cleared
      ? { operator: '', value: '', end_value: '' }
      : { ...joinedDate, ...next };
    setJoinedDate(merged);
    syncFilters({
      audience: 'filtered', audience_type: audienceType,
      countries, segments, territory,
      joined_date: cleared ? { operator: '', value: '', end_value: '' } : merged,
    });
  };

  if (loading) return <div className="p-6 text-white/50 text-sm">Loading campaign…</div>;
  if (!campaign) return <div className="p-6 text-white/50 text-sm">Campaign not found.</div>;

  return (
    <div className="p-6" data-testid="campaign-editor-page">
      <Toaster position="top-right" theme="dark" />

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-6">
        <div>
          <Link to="/admin/campaigns" className="inline-flex items-center gap-1 text-white/40 hover:text-white text-sm mb-2" data-testid="back-to-campaigns">
            <ArrowLeft className="w-4 h-4" /> Back to Campaigns
          </Link>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={readOnly}
            placeholder="Campaign name (internal only)"
            className="text-2xl font-bold text-white bg-transparent border-0 outline-none focus:border-b focus:border-[#00D4FF]/40 min-w-[320px]"
            data-testid="campaign-name-input"
          />
          <div className="mt-1 text-xs text-white/40">Status: <span className="uppercase tracking-wider">{campaign.status}</span></div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={openPreview} className="btn-secondary inline-flex items-center gap-2" data-testid="preview-btn">
            <Eye className="w-4 h-4" /> Preview
          </button>
          <button onClick={() => handleSave(false)} disabled={saving || readOnly} className="btn-secondary inline-flex items-center gap-2 disabled:opacity-50" data-testid="save-draft-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Draft
          </button>
          <button
            onClick={openSaveTemplate}
            disabled={readOnly}
            className="btn-secondary inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="save-as-template-btn"
            title="Save the current subject + preheader + body as a reusable template"
          >
            <Bookmark className="w-4 h-4" /> Save as Template
          </button>
          {/* Phase 3 — Recipient Preview is mandatory for customers/suppliers
              audience. Button always available; shows hash mismatch / confirm
              state in its label. */}
          {(audienceType === 'customers' || audienceType === 'suppliers' || audienceType === 'prospects') && (
            <button
              onClick={openRecipientPreview}
              disabled={readOnly}
              className="btn-secondary inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="open-recipient-preview-btn"
            >
              <Users className="w-4 h-4" />
              {previewConfirmedHash && previewData && previewConfirmedHash === previewData.filters_hash
                ? `Preview Confirmed (${previewData.counts.final ?? 0})`
                : 'Recipient Preview'}
            </button>
          )}
          <button
            onClick={handleSendNow}
            disabled={(() => {
              if (sending || readOnly || !subject || !bodyHtml || sendGateBlocked) return true;
              // For Phase-3 / Feb-2026 master audiences (customers /
              // suppliers / prospects) the legacy `audienceBreakdown`
              // endpoint always returns 0 — use the confirmed
              // recipient-preview count instead.
              if (audienceType === 'customers' || audienceType === 'suppliers' || audienceType === 'prospects') {
                return (previewData?.counts?.final ?? 0) === 0;
              }
              return (breakdown?.deduplicated_recipients ?? audienceSummary.count) === 0;
            })()}
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="send-campaign-btn"
            title={sendGateBlocked ? 'Open and confirm the Recipient Preview before sending' : ''}
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {(() => {
              const isMaster = audienceType === 'customers' || audienceType === 'suppliers' || audienceType === 'prospects';
              if (!isMaster) {
                return `Send to ${breakdown?.deduplicated_recipients ?? audienceSummary.count}`;
              }
              // Aug-2026 — master audience labels are authoritative from
              // Recipient Preview only. Any legacy fallback (subscribers
              // count) is misleading because it doesn't apply the
              // audience filters. When the preview is missing / stale
              // we prompt the operator to (re-)confirm; the button is
              // already disabled by sendGateBlocked so this is a label
              // change only.
              const valid = previewData && previewData.filters_hash
                && previewConfirmedHash === previewData.filters_hash;
              const kindLabel = audienceType === 'customers' ? 'Customers'
                : audienceType === 'suppliers' ? 'Suppliers'
                : 'Prospects';
              if (!previewData) return 'Preview Recipients';
              if (!valid) return 'Re-confirm Preview';
              return `Send to ${previewData.counts.final ?? 0} ${kindLabel}`;
            })()}
          </button>
        </div>
      </div>

      {readOnly && (
        <div className="border border-amber-400/30 bg-amber-400/5 text-amber-400 text-xs p-3 mb-4 inline-flex items-center gap-2">
          <AlertCircle className="w-4 h-4" /> This campaign has already been sent — editing is disabled.
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-6">
        {/* Left column: editor */}
        <div className="space-y-5">
          {/* Templates row */}
          {!readOnly && (
            <div className="border border-white/10 bg-[#0A0A0A] p-4" data-testid="templates-row">
              <div className="text-[10px] font-semibold tracking-widest uppercase text-white/55 mb-2">Start with a template</div>
              <div className="flex flex-wrap gap-2">
                {templates.map((t) => (
                  <button
                    key={t.key}
                    onClick={() => applyTemplate(t.key)}
                    className="text-left border border-white/10 hover:border-[#00D4FF]/40 bg-[#050505] p-3 transition-colors"
                    data-testid={`template-pick-${t.key}`}
                  >
                    <div className="text-white text-sm font-medium">{t.name}</div>
                    <div className="text-[11px] text-white/40 mt-1 max-w-[230px]">{t.description}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Subject + preheader */}
          <div className="space-y-3">
            <div>
              <label className="text-[10px] font-semibold tracking-widest uppercase text-white/55 mb-1 block flex items-center justify-between">
                <span>From (Sender Profile)</span>
                <Link to="/admin/sender-profiles" className="normal-case tracking-normal text-[#00D4FF] text-[11px] hover:underline">
                  Manage profiles →
                </Link>
              </label>
              <select
                value={senderProfileId}
                onChange={(e) => setSenderProfileId(e.target.value)}
                disabled={readOnly}
                className="w-full px-3 py-2.5 bg-[#050505] border border-white/10 focus:border-[#00D4FF] outline-none text-white text-sm"
                data-testid="campaign-sender-profile-select"
              >
                {senderProfiles.length === 0 && <option value="">Default sender (loading…)</option>}
                {senderProfiles.filter((p) => p.is_active || p.id === senderProfileId).map((p) => (
                  <option key={p.id} value={p.id} data-testid={`sender-option-${p.key}`}>
                    {p.display_name} &lt;{p.email}&gt;{p.is_default ? ' — default' : ''}{!p.is_active ? ' (inactive)' : ''}
                  </option>
                ))}
              </select>
              {senderProfileId && (
                <div className="text-[11px] text-white/40 mt-1">
                  Inbox will display: <span className="text-white/70 font-medium">
                    {(senderProfiles.find((p) => p.id === senderProfileId) || {}).display_name}
                  </span> &lt;<span className="text-white/70">
                    {(senderProfiles.find((p) => p.id === senderProfileId) || {}).email}
                  </span>&gt;
                </div>
              )}
            </div>
            <div>
              <label className="text-[10px] font-semibold tracking-widest uppercase text-white/55 mb-1 block">Subject Line</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                disabled={readOnly}
                placeholder="e.g. Luxor Tek — This Week's Wholesale Inventory"
                className="w-full px-3 py-2.5 bg-[#050505] border border-white/10 focus:border-[#00D4FF] outline-none text-white text-sm"
                data-testid="campaign-subject-input"
              />
            </div>
            <div>
              <label className="text-[10px] font-semibold tracking-widest uppercase text-white/55 mb-1 block">
                Preheader <span className="text-white/30 normal-case tracking-normal">(preview text — first ~80 chars shown in inbox)</span>
              </label>
              <input
                type="text"
                value={preheader}
                onChange={(e) => setPreheader(e.target.value)}
                disabled={readOnly}
                maxLength={140}
                placeholder="Optional but improves open rate"
                className="w-full px-3 py-2.5 bg-[#050505] border border-white/10 focus:border-[#00D4FF] outline-none text-white text-sm"
                data-testid="campaign-preheader-input"
              />
            </div>
          </div>

          {/* Body */}
          <div>
            <label className="text-[10px] font-semibold tracking-widest uppercase text-white/55 mb-1 block">
              Email Body
              <span className="text-white/30 normal-case tracking-normal ml-2">— use {`{first_name}`}, {`{company}`}, {`{country}`} to personalize</span>
            </label>
            <RichTextEditor
              value={bodyHtml}
              onChange={setBodyHtml}
              testid="campaign-editor-rte"
            />
          </div>

          {/* Test send */}
          <div className="border border-white/10 bg-[#0A0A0A] p-4 flex flex-wrap items-end gap-3" data-testid="test-send-row">
            <div className="flex-1 min-w-[240px]">
              <label className="text-[10px] font-semibold tracking-widest uppercase text-white/55 mb-1 block">Send Test To</label>
              <input
                type="email"
                value={testEmailTo}
                onChange={(e) => setTestEmailTo(e.target.value)}
                placeholder="your@email.com"
                className="w-full px-3 py-2.5 bg-[#050505] border border-white/10 focus:border-[#00D4FF] outline-none text-white text-sm"
                data-testid="test-email-input"
              />
            </div>
            <button
              onClick={handleTestSend}
              disabled={sendingTest || !subject || !bodyHtml}
              className="btn-secondary inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="send-test-btn"
            >
              {sendingTest ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              Send Test Email
            </button>
          </div>

          {/* D1.8 — Test send result chip */}
          {testResult && (
            <div
              data-testid="test-send-result"
              data-status={testResult.status}
              className={`border p-3 text-xs flex items-start gap-3 ${
                testResult.status === 'sent' || testResult.status === 'delivered'
                  ? 'border-emerald-500/40 bg-emerald-500/5 text-emerald-200'
                  : testResult.status === 'queued'
                    ? 'border-amber-500/40 bg-amber-500/5 text-amber-200'
                    : 'border-red-500/40 bg-red-500/5 text-red-200'
              }`}
            >
              <div className="font-semibold uppercase tracking-widest text-[10px] mt-0.5 shrink-0" data-testid="test-send-result-status">
                {testResult.status === 'sent' || testResult.status === 'delivered'
                  ? 'Delivered'
                  : testResult.status === 'queued' ? 'Queued' : 'Failed'}
              </div>
              <div className="flex-1 min-w-0 break-words">
                <div>→ <span className="font-mono">{testResult.to}</span></div>
                {testResult.message_id && (
                  <div className="opacity-70 mt-0.5">message-id: <span className="font-mono">{testResult.message_id}</span></div>
                )}
                {testResult.profile_email && (
                  <div className="opacity-70 mt-0.5">via: <span className="font-mono">{testResult.profile_email}</span></div>
                )}
                {testResult.sent_at && (
                  <div className="opacity-70 mt-0.5">sent_at: {new Date(testResult.sent_at).toLocaleString()}</div>
                )}
                {testResult.error && (
                  <div className="text-red-300 mt-1 font-mono whitespace-pre-wrap">{testResult.error}</div>
                )}
                <div className="opacity-50 mt-1.5 text-[10px]">
                  ⓘ Test sends now use the EXACT same pipeline as real campaigns (List-Unsubscribe headers, click tracking, render). Excluded from analytics &amp; Hot Leads.
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right column: sidebar */}
        <div className="space-y-5">
          {/* Audience filter */}
          <div className="border border-white/10 bg-[#0A0A0A] p-4" data-testid="audience-panel">
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-4 h-4 text-[#00D4FF]" />
              <div className="text-sm font-semibold text-white">Audience</div>
              <span
                className="ml-auto text-[#00D4FF] font-bold text-lg"
                data-testid="audience-count"
                title={
                  (audienceType === 'customers' || audienceType === 'suppliers' || audienceType === 'prospects')
                    ? (previewData && previewData.filters_hash && previewConfirmedHash === previewData.filters_hash
                        ? 'Confirmed Recipient Preview count'
                        : 'Open Recipient Preview to compute the exact send-set after safety exclusions.')
                    : 'Live subscribers matching your filters'
                }
              >
                {(() => {
                  // Aug-2026 — master audiences (customers / suppliers /
                  // prospects) must derive their audience count from
                  // Recipient Preview only, so the count matches the
                  // send-set after exclusions (Manual Review, Direct
                  // Personal Follow-Up, Exclude, duplicates, opt-outs,
                  // invalid, bounces, already-contacted). Any legacy
                  // fallback (audienceSummary.count / breakdown) is a
                  // subscribers-based number and can differ wildly.
                  const isMaster = audienceType === 'customers' || audienceType === 'suppliers' || audienceType === 'prospects';
                  if (isMaster) {
                    const valid = previewData && previewData.filters_hash
                      && previewConfirmedHash === previewData.filters_hash;
                    return valid ? (previewData.counts.final ?? 0) : '—';
                  }
                  return breakdown?.deduplicated_recipients ?? audienceSummary.count;
                })()}
              </span>
            </div>
            <div className="text-[11px] text-white/40 mb-3">Live recipients matching your filters.</div>

            {/* Audience type selector */}
            <div className="mb-3">
              <div className="text-[10px] font-semibold tracking-widest uppercase text-white/55 mb-2">Audience Type</div>
              <select
                value={audienceType}
                onChange={(e) => {
                  const next = e.target.value;
                  setAudienceType(next);
                  // Phase-3 master types persist their own filter blocks;
                  // legacy types still use the old country/segment/territory.
                  if (next === 'customers' || next === 'suppliers' || next === 'prospects') {
                    // Master audiences ALWAYS use "By Filter" — the
                    // legacy All-Subscribers toggle would silently
                    // bypass filters (including the prospect
                    // qualification safety filters), so force
                    // filtered mode as soon as one is selected.
                    setAudience('filtered');
                    syncFilters({
                      audience: 'filtered', audience_type: next,
                      countries: [], segments: [], territory: '',
                    });
                  } else {
                    syncFilters({
                      audience, audience_type: next,
                      countries: audience === 'filtered' ? countries : [],
                      segments: audience === 'filtered' ? segments : [],
                      territory,
                    });
                  }
                  setPreviewConfirmedHash('');  // any audience change invalidates the gate
                }}
                disabled={readOnly}
                className="ipt-select w-full px-3 py-2 bg-[#050505] border border-white/10 text-white text-sm focus:outline-none focus:border-[#00D4FF]/50 disabled:opacity-40"
                data-testid="audience-type-select"
              >
                <optgroup label="Master Audiences (Phase 3)">
                  <option value="customers">Customers</option>
                  <option value="suppliers">Suppliers</option>
                  <option value="prospects">Prospects ({prospectOptions.total_prospects || 0})</option>
                </optgroup>
                <optgroup label="Legacy Audiences">
                  <option value="subscribers">Subscribers (legacy)</option>
                  <option value="leads">Leads (legacy)</option>
                  <option value="buyers">Buyers (legacy)</option>
                  <option value="all">All Marketing Contacts (legacy)</option>
                </optgroup>
              </select>
              <div className="text-[10px] text-white/30 mt-1.5 leading-snug">
                {audienceType === 'customers' && 'Master Customers — unified, deduped, opt-in-aware. Use the filters below to narrow.'}
                {audienceType === 'suppliers' && 'Master Suppliers — separate from customers. Always treated as opted-in.'}
                {audienceType === 'prospects' && 'Master Prospects (new). Filter by country / source / import batch / outreach status / tier. Use recipient limit + skip-already-contacted to send in batches.'}
                {audienceType === 'subscribers' && 'Weekly Inventory subscribers, excluding unsubscribed.'}
                {audienceType === 'leads' && 'Wholesale leads where marketing_opt_in is true. Excludes unsubscribed.'}
                {audienceType === 'buyers' && 'Approved buyers (missing opt-in treated as opted-in). Excludes unsubscribed.'}
                {audienceType === 'all' && 'Subscribers + opted-in Leads + opted-in Prospects + Buyers. Deduplicated by email.'}
              </div>
            </div>

            {/* ─── Phase 3 — Customer Filters ─────────────────────── */}
            {audienceType === 'customers' && (
              <div className="mb-3 p-3 border border-[#00D4FF]/20 bg-[#00D4FF]/[0.03]" data-testid="customer-filters-panel">
                <div className="text-[10px] font-semibold tracking-widest uppercase text-[#00D4FF] mb-2">Customer Filters</div>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <CustSel label="Language" value={customerFilters.language} options={custEnums.languages} onChange={(v) => { setCustomerFilters({ ...customerFilters, language: v }); setPreviewConfirmedHash(''); }} testid="cf-language" />
                  <CustSel label="Stage" value={customerFilters.stage} options={custEnums.stages} onChange={(v) => { setCustomerFilters({ ...customerFilters, stage: v }); setPreviewConfirmedHash(''); }} testid="cf-stage" />
                  <CustSel label="Source" value={customerFilters.source} options={custEnums.sources} onChange={(v) => { setCustomerFilters({ ...customerFilters, source: v }); setPreviewConfirmedHash(''); }} testid="cf-source" />
                  <CustInput label="Segment" value={customerFilters.segment} onChange={(v) => { setCustomerFilters({ ...customerFilters, segment: v }); setPreviewConfirmedHash(''); }} testid="cf-segment" />
                  <CustInput label="Import Batch" value={customerFilters.import_batch} onChange={(v) => { setCustomerFilters({ ...customerFilters, import_batch: v }); setPreviewConfirmedHash(''); }} testid="cf-import-batch" placeholder="exact match" />
                  <CustInput label="Country" value={customerFilters.country} onChange={(v) => { setCustomerFilters({ ...customerFilters, country: v }); setPreviewConfirmedHash(''); }} testid="cf-country" />
                  <CustSel label="Email Opt-In" value={customerFilters.email_opt_in} options={['true', 'false']} onChange={(v) => { setCustomerFilters({ ...customerFilters, email_opt_in: v }); setPreviewConfirmedHash(''); }} testid="cf-opt-in" />
                  <CustInput label="Search" value={customerFilters.search} onChange={(v) => { setCustomerFilters({ ...customerFilters, search: v }); setPreviewConfirmedHash(''); }} testid="cf-search" placeholder="email / company..." />
                </div>
                <DateOpRow label="Last Contact" value={customerFilters.last_contact} onChange={(v) => { setCustomerFilters({ ...customerFilters, last_contact: v }); setPreviewConfirmedHash(''); }} testid="cf-last-contact" />
                <DateOpRow label="Last Campaign Sent" value={customerFilters.last_campaign_sent} onChange={(v) => { setCustomerFilters({ ...customerFilters, last_campaign_sent: v }); setPreviewConfirmedHash(''); }} testid="cf-last-campaign" />
                <div className="text-[10px] text-white/40 mt-2">Empty filters target ALL customers. Click &quot;Recipient Preview&quot; to compute exact recipient set.</div>
              </div>
            )}

            {/* ─── Phase 3 — Supplier Filters ────────────────────── */}
            {audienceType === 'suppliers' && (
              <div className="mb-3 p-3 border border-[#00D4FF]/20 bg-[#00D4FF]/[0.03]" data-testid="supplier-filters-panel">
                <div className="text-[10px] font-semibold tracking-widest uppercase text-[#00D4FF] mb-2">Supplier Filters</div>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <CustInput label="Country" value={supplierFilters.country} onChange={(v) => { setSupplierFilters({ ...supplierFilters, country: v }); setPreviewConfirmedHash(''); }} testid="sf-country" />
                  <CustInput label="Category" value={supplierFilters.category} onChange={(v) => { setSupplierFilters({ ...supplierFilters, category: v }); setPreviewConfirmedHash(''); }} testid="sf-category" placeholder="e.g. Laptops" />
                  <CustInput label="Ranking" value={supplierFilters.ranking} onChange={(v) => { setSupplierFilters({ ...supplierFilters, ranking: v }); setPreviewConfirmedHash(''); }} testid="sf-ranking" placeholder="A / B / C" />
                  <CustInput label="Tier" value={supplierFilters.supplier_tier} onChange={(v) => { setSupplierFilters({ ...supplierFilters, supplier_tier: v }); setPreviewConfirmedHash(''); }} testid="sf-tier" />
                  <CustInput label="Search" value={supplierFilters.search} onChange={(v) => { setSupplierFilters({ ...supplierFilters, search: v }); setPreviewConfirmedHash(''); }} testid="sf-search" placeholder="email / company..." />
                </div>
                <DateOpRow label="Last Contact" value={supplierFilters.last_contact} onChange={(v) => { setSupplierFilters({ ...supplierFilters, last_contact: v }); setPreviewConfirmedHash(''); }} testid="sf-last-contact" />
                <div className="text-[10px] text-white/40 mt-2">Suppliers are kept separate from Customers. Always treated as opted-in.</div>
              </div>
            )}

            {/* ─── Feb-2026 — Prospect Filters + batch-send controls ── */}
            {audienceType === 'prospects' && (
              <div className="mb-3 p-3 border border-[#00D4FF]/20 bg-[#00D4FF]/[0.03]" data-testid="prospect-filters-panel">
                <div className="text-[10px] font-semibold tracking-widest uppercase text-[#00D4FF] mb-2">Prospect Filters</div>

                {/* Qualification block — required for Prospect campaign sending. */}
                <div className="mb-2" data-testid="pf-qualification-block">
                  <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1.5">
                    Qualification <span className="text-white/30 normal-case tracking-normal">(multi-select within a field = OR, across fields = AND)</span>
                  </div>
                {/* Qualification Confidence chip is now inside Advanced.
                    Remove the standalone dropdown from the top row so we
                    don't render the same filter twice. */}
                  <div className="grid grid-cols-3 gap-2">
                    <CountryChecklist
                      label="Campaign Audience *"
                      values={prospectFilters.campaign_audiences}
                      options={CAMPAIGN_AUDIENCE_OPTIONS}
                      onChange={(next) => {
                        setProspectFilters({ ...prospectFilters, campaign_audiences: next });
                        setPreviewConfirmedHash('');
                      }}
                      testid="pf-campaign-audience"
                    />
                    <CountryChecklist
                      label="Campaign Eligibility *"
                      values={prospectFilters.campaign_eligibilities}
                      options={CAMPAIGN_ELIGIBILITY_OPTIONS}
                      onChange={(next) => {
                        setProspectFilters({ ...prospectFilters, campaign_eligibilities: next });
                        setPreviewConfirmedHash('');
                      }}
                      testid="pf-campaign-eligibility"
                    />
                    <div />
                  </div>
                  {((prospectFilters.campaign_audiences || []).length === 0
                    || (prospectFilters.campaign_eligibilities || []).length === 0) && (
                    <div className="text-[10px] text-amber-300/80 mt-1.5 leading-snug" data-testid="pf-qualification-warning">
                      Campaign Audience and Campaign Eligibility are required before sending a Prospect campaign.
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2 mb-2">
                  {/* Feb-2026 — Language + Market front-and-centre. Language
                      is a persistent prospect field; Market is derived from
                      country. Both are multi-select OR-match filters. */}
                  <CountryChecklist
                    label="Language"
                    values={prospectFilters.languages}
                    options={prospectOptions.languages || []}
                    onChange={(next) => {
                      setProspectFilters({ ...prospectFilters, languages: next });
                      setPreviewConfirmedHash('');
                    }}
                    testid="pf-language"
                  />
                  <CountryChecklist
                    label="Market"
                    values={prospectFilters.markets}
                    options={prospectOptions.markets || []}
                    onChange={(next) => {
                      setProspectFilters({ ...prospectFilters, markets: next });
                      setPreviewConfirmedHash('');
                    }}
                    testid="pf-market"
                  />
                  <CustSel label="Outreach Status" value={prospectFilters.outreach_status} options={prospectOptions.outreach_statuses || []} onChange={(v) => { setProspectFilters({ ...prospectFilters, outreach_status: v }); setPreviewConfirmedHash(''); }} testid="pf-outreach-status" />
                  <CustSel label="Buyer Potential Tier" value={prospectFilters.buyer_potential_tier} options={prospectOptions.buyer_potential_tiers || []} onChange={(v) => { setProspectFilters({ ...prospectFilters, buyer_potential_tier: v }); setPreviewConfirmedHash(''); }} testid="pf-tier" />
                  <CustInput label="Search" value={prospectFilters.search} onChange={(v) => { setProspectFilters({ ...prospectFilters, search: v }); setPreviewConfirmedHash(''); }} testid="pf-search" placeholder="company / email / website" />
                </div>

                {/* Advanced Filters — Country / Source / Import Batch /
                    Qualification Confidence. Collapsed by default so the
                    main panel stays scannable. */}
                <div className="border border-white/10 bg-black/40 mb-2" data-testid="pf-advanced-wrap">
                  <button
                    type="button"
                    onClick={() => setAdvancedFiltersOpen((v) => !v)}
                    className="w-full flex items-center justify-between px-3 py-2 text-[11px] uppercase tracking-widest text-white/50 hover:text-white transition-colors"
                    data-testid="pf-advanced-toggle"
                  >
                    <span>Advanced Filters</span>
                    <span className="text-white/40">{advancedFiltersOpen ? '▾' : '▸'}</span>
                  </button>
                  {advancedFiltersOpen && (
                    <div className="grid grid-cols-2 gap-2 p-3 pt-0" data-testid="pf-advanced-body">
                      <CountryChecklist
                        label="Country"
                        values={prospectFilters.countries}
                        options={prospectOptions.countries || []}
                        onChange={(next) => {
                          setProspectFilters({ ...prospectFilters, countries: next });
                          setPreviewConfirmedHash('');
                        }}
                        testid="pf-country"
                      />
                      <CustSel label="Source" value={prospectFilters.source} options={prospectOptions.sources || []} onChange={(v) => { setProspectFilters({ ...prospectFilters, source: v }); setPreviewConfirmedHash(''); }} testid="pf-source" />
                      <CustSel label="Import Batch" value={prospectFilters.import_batch} options={prospectOptions.import_batches || []} onChange={(v) => { setProspectFilters({ ...prospectFilters, import_batch: v }); setPreviewConfirmedHash(''); }} testid="pf-import-batch" />
                      <CountryChecklist
                        label="Qualification Confidence"
                        values={prospectFilters.qualification_confidences}
                        options={QUALIFICATION_CONFIDENCE_OPTIONS}
                        onChange={(next) => {
                          setProspectFilters({ ...prospectFilters, qualification_confidences: next });
                          setPreviewConfirmedHash('');
                        }}
                        testid="pf-qualification-confidence-adv"
                      />
                    </div>
                  )}
                </div>
                {/* Batch-send controls */}
                <div className="mt-3 pt-3 border-t border-white/10 grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider text-white/55 mb-1">Recipient limit (batch size)</label>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={recipientLimit}
                      onChange={(e) => { setRecipientLimit(e.target.value); setPreviewConfirmedHash(''); }}
                      placeholder="e.g. 75 — leave empty for all"
                      className="w-full px-3 py-2 bg-[#050505] border border-white/10 text-white text-sm focus:outline-none focus:border-[#00D4FF]/50"
                      data-testid="pf-recipient-limit"
                    />
                    <div className="text-[10px] text-white/35 mt-1">Sends only the first N matching prospects, ordered by company name + id (deterministic).</div>
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider text-white/55 mb-1">Batch safety</label>
                    <label className="flex items-center gap-2 cursor-pointer mt-1.5">
                      <input
                        type="checkbox"
                        checked={skipAlreadyContacted}
                        onChange={(e) => { setSkipAlreadyContacted(e.target.checked); setPreviewConfirmedHash(''); }}
                        className="accent-[#00D4FF]"
                        data-testid="pf-skip-already-contacted"
                      />
                      <span className="text-sm text-white/80">Skip already-contacted prospects</span>
                    </label>
                    <div className="text-[10px] text-white/35 mt-1">When ON, any prospect that received ANY prior campaign is excluded — even if filters now match again. Required for safe batch sending.</div>
                  </div>
                </div>
                <div className="text-[10px] text-white/40 mt-3">{prospectOptions.total_prospects || 0} prospects in master. Click &quot;Recipient Preview&quot; to compute the exact send-set after filters + exclusions.</div>

                {/* Feb-2026 — Compact Audience Preview. Reads from the
                    last confirmed Recipient Preview so we don't hit the
                    server again for a summary. Shows total eligible +
                    language/market breakdown + excluded counts. */}
                {previewData && previewData.counts && (
                  <div className="mt-3 pt-3 border-t border-white/10" data-testid="pf-audience-preview">
                    <div className="text-[10px] uppercase tracking-widest text-[#00D4FF] mb-2">Audience Preview</div>
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div>
                        <div className="text-white/50 mb-1">Total eligible</div>
                        <div className="text-[#00D4FF] font-bold text-lg" data-testid="ap-total">
                          {previewData.counts.final ?? 0}
                        </div>
                      </div>
                      <div className="text-[10px] text-white/50 leading-relaxed">
                        <div className="flex justify-between" data-testid="ap-excluded-contacted">
                          <span>Already-contacted excluded</span>
                          <span className="text-yellow-300">−{previewData.counts.already_contacted_removed ?? 0}</span>
                        </div>
                        <div className="flex justify-between" data-testid="ap-excluded-unsubscribed">
                          <span>Unsubscribed excluded</span>
                          <span className="text-yellow-300">−{previewData.counts.unsubscribed_removed ?? 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Bounced excluded</span>
                          <span className="text-yellow-300">−{previewData.counts.bounced_removed ?? 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Invalid email excluded</span>
                          <span className="text-yellow-300">−{previewData.counts.invalid_emails_removed ?? 0}</span>
                        </div>
                      </div>
                    </div>
                    {previewData.distribution && (
                      <div className="grid grid-cols-2 gap-3 mt-3 text-[11px]">
                        <div data-testid="ap-language-breakdown">
                          <div className="text-white/50 uppercase tracking-widest text-[10px] mb-1">Language</div>
                          {Object.entries(previewData.distribution.language || {}).map(([k, v]) => (
                            <div key={k} className="flex justify-between py-0.5">
                              <span className="text-white/70">{k}</span>
                              <span className="text-white">{v}</span>
                            </div>
                          ))}
                        </div>
                        <div data-testid="ap-market-breakdown">
                          <div className="text-white/50 uppercase tracking-widest text-[10px] mb-1">Market</div>
                          {Object.entries(previewData.distribution.market || {}).map(([k, v]) => (
                            <div key={k} className="flex justify-between py-0.5">
                              <span className="text-white/70">{k}</span>
                              <span className="text-white">{v}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}


            {/* Market filter (applies only to prospects in the audience) */}
            <div className="mb-3" data-testid="territory-panel">
              <div className="text-[10px] font-semibold tracking-widest uppercase text-white/55 mb-2">Market</div>
              <select
                value={territory}
                onChange={(e) => changeTerritory(e.target.value)}
                disabled={readOnly}
                className="ipt-select w-full px-3 py-2 bg-[#050505] border border-white/10 text-white text-sm focus:outline-none focus:border-[#00D4FF]/50 disabled:opacity-40"
                data-testid="campaign-territory-select"
              >
                <option value="">All markets</option>
                <option value="Mexico">Mexico</option>
                <option value="Central America">Central America</option>
                <option value="South America">South America</option>
                <option value="Caribbean">Caribbean</option>
                <option value="Multi-Country">Multi-Country</option>
              </select>
              {territory && (
                <div className="text-[10px] text-white/30 mt-1.5 leading-snug">
                  Applies only to prospects with market = <span className="text-white/55">{territory}</span>. Other sources (subscribers, leads, buyers) are not filtered by market.
                </div>
              )}
            </div>

            {/* Master audiences always send "By Filter" — the
                All-Subscribers chip would bypass the qualification
                safety filters on prospects, so we hide the toggle for
                Customers / Suppliers / Prospects. */}
            {!(audienceType === 'customers' || audienceType === 'suppliers' || audienceType === 'prospects') && (
              <div className="flex gap-2 mb-3">
                <Chip active={audience === 'all'} onClick={() => setAudienceMode('all')} testid="audience-all-btn">All Subscribers</Chip>
                <Chip active={audience === 'filtered'} onClick={() => setAudienceMode('filtered')} testid="audience-filtered-btn">By Filter</Chip>
              </div>
            )}

            {audience === 'filtered' && (
              <>
                <div className="mb-4">
                  <div className="text-[10px] font-semibold tracking-widest uppercase text-white/55 mb-2">Countries</div>
                  {allCountries.length === 0 ? (
                    <div className="text-xs text-white/40">No subscriber countries yet.</div>
                  ) : (
                    <CountryChecklist
                      label=""
                      values={countries}
                      options={allCountries}
                      onChange={(next) => {
                        // Replace the entire selection at once so the
                        // multi-select dropdown drives the same
                        // `toggleCountry` state the old chips did.
                        const additions = next.filter((c) => !countries.includes(c));
                        const removals = countries.filter((c) => !next.includes(c));
                        additions.concat(removals).forEach((c) => toggleCountry(c));
                      }}
                      testid="sub-country-multi"
                    />
                  )}
                  {countries.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2" data-testid="countries-chips">
                      {countries.map((c) => (
                        <span
                          key={c}
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] border border-[#00D4FF]/40 text-[#00D4FF] bg-[#00D4FF]/10"
                          data-testid={`country-chip-${c}`}
                        >
                          {c}
                          <button
                            type="button"
                            onClick={() => toggleCountry(c)}
                            className="text-[#00D4FF]/70 hover:text-white ml-0.5"
                            aria-label={`Remove ${c}`}
                          >×</button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <div className="text-[10px] font-semibold tracking-widest uppercase text-white/55 mb-2">Buyer Segments</div>
                  <div className="flex flex-wrap gap-2" data-testid="segments-chips">
                    {allSegments.map((s) => (
                      <Chip key={s} active={segments.includes(s)} onClick={() => toggleSegment(s)} testid={`segment-chip-${s}`}>
                        {s}
                      </Chip>
                    ))}
                  </div>
                </div>

                {/* Subscriber Filters — Joined Date (Feb-18). Applies
                    to the subscribers source (or the subscribers slice
                    of audience_type=all). Lets admins send to only
                    newly imported subscribers without clobbering the
                    rest of the list. */}
                {(audienceType === 'subscribers' || audienceType === 'all') && (
                  <div className="mt-4 pt-3 border-t border-white/5" data-testid="subscriber-filters-panel">
                    <div className="text-[10px] font-semibold tracking-widest uppercase text-white/55 mb-2">
                      Subscriber Filters
                    </div>
                    <label className="text-[10px] uppercase tracking-wider text-white/40 mb-1.5 block">
                      Joined Date
                    </label>
                    <div className="flex flex-wrap items-center gap-2">
                      <select
                        value={joinedDate.operator}
                        onChange={(e) => changeJoinedDate({ operator: e.target.value })}
                        disabled={readOnly}
                        className="px-2 py-1.5 bg-[#050505] border border-white/10 text-white text-xs focus:outline-none focus:border-[#00D4FF]/50 disabled:opacity-40"
                        data-testid="joined-date-operator"
                      >
                        <option value="">Any time</option>
                        <option value="today">Today</option>
                        <option value="yesterday">Yesterday</option>
                        <option value="last_7_days">Last 7 Days</option>
                        <option value="last_30_days">Last 30 Days</option>
                        <option value="is">Is</option>
                        <option value="before">Before</option>
                        <option value="after">After</option>
                        <option value="between">Between</option>
                      </select>
                      {['is', 'before', 'after', 'between'].includes(joinedDate.operator) && (
                        <input
                          type="date"
                          value={joinedDate.value}
                          onChange={(e) => changeJoinedDate({ value: e.target.value })}
                          disabled={readOnly}
                          className="px-2 py-1.5 bg-[#050505] border border-white/10 text-white text-xs focus:outline-none focus:border-[#00D4FF]/50 disabled:opacity-40"
                          data-testid="joined-date-value"
                        />
                      )}
                      {joinedDate.operator === 'between' && (
                        <>
                          <span className="text-white/30 text-xs">to</span>
                          <input
                            type="date"
                            value={joinedDate.end_value}
                            onChange={(e) => changeJoinedDate({ end_value: e.target.value })}
                            disabled={readOnly}
                            className="px-2 py-1.5 bg-[#050505] border border-white/10 text-white text-xs focus:outline-none focus:border-[#00D4FF]/50 disabled:opacity-40"
                            data-testid="joined-date-end-value"
                          />
                        </>
                      )}
                      {joinedDate.operator && (
                        <button
                          type="button"
                          onClick={() => changeJoinedDate({ operator: '' })}
                          disabled={readOnly}
                          className="px-2 py-1 text-[10px] border border-white/10 text-white/40 hover:text-white hover:border-white/30 disabled:opacity-40"
                          data-testid="joined-date-clear"
                        >Clear</button>
                      )}
                    </div>
                    {joinedDate.operator && (
                      <div className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 text-[11px] border border-[#00D4FF]/30 text-[#00D4FF] bg-[#00D4FF]/10" data-testid="joined-date-active-chip">
                        Joined Date:{' '}
                        {joinedDate.operator === 'today' && 'Today'}
                        {joinedDate.operator === 'yesterday' && 'Yesterday'}
                        {joinedDate.operator === 'last_7_days' && 'Last 7 Days'}
                        {joinedDate.operator === 'last_30_days' && 'Last 30 Days'}
                        {joinedDate.operator === 'is' && (joinedDate.value || '—')}
                        {joinedDate.operator === 'before' && `Before ${joinedDate.value || '—'}`}
                        {joinedDate.operator === 'after' && `After ${joinedDate.value || '—'}`}
                        {joinedDate.operator === 'between' && `${joinedDate.value || '—'} to ${joinedDate.end_value || '—'}`}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {audienceSummary.by_country?.length > 0 && (
              <div className="mt-4 pt-3 border-t border-white/5">
                <div className="text-[10px] font-semibold tracking-widest uppercase text-white/55 mb-2">Top Countries in Audience</div>
                {audienceSummary.by_country.slice(0, 5).map(([c, n]) => (
                  <div key={c} className="flex items-center justify-between text-xs text-white/55 py-0.5">
                    <span>{c}</span><span className="text-white">{n}</span>
                  </div>
                ))}
              </div>
            )}

            {breakdown && (
              <div className="mt-4 pt-3 border-t border-white/5" data-testid="audience-breakdown">
                <div className="text-[10px] font-semibold tracking-widest uppercase text-white/55 mb-2">Pre-send Breakdown</div>
                <div className="space-y-1 text-xs">
                  <div className="flex items-center justify-between"><span className="text-white/55">Subscribers</span><span className="text-white" data-testid="bd-subscribers">{breakdown.subscribers}</span></div>
                  <div className="flex items-center justify-between"><span className="text-white/55">Leads</span><span className="text-white" data-testid="bd-leads">{breakdown.leads}</span></div>
                  <div className="flex items-center justify-between"><span className="text-white/55">Prospects</span><span className="text-white" data-testid="bd-prospects">{breakdown.prospects}</span></div>
                  <div className="flex items-center justify-between"><span className="text-white/55">Buyers</span><span className="text-white" data-testid="bd-buyers">{breakdown.buyers}</span></div>
                  <div className="flex items-center justify-between pt-1.5 mt-1.5 border-t border-white/5"><span className="text-white/55">Total Raw Contacts</span><span className="text-white font-medium" data-testid="bd-total-raw">{breakdown.total_raw}</span></div>
                  <div className="flex items-center justify-between"><span className="text-[#00D4FF]/80 font-semibold">Deduplicated Recipients</span><span className="text-[#00D4FF] font-bold" data-testid="bd-deduplicated">{breakdown.deduplicated_recipients}</span></div>
                  {breakdown.unsubscribed_excluded > 0 && (
                    <div className="flex items-center justify-between text-[10px] mt-1"><span className="text-yellow-400/80">Unsubscribed (excluded)</span><span className="text-yellow-400" data-testid="bd-unsubscribed">−{breakdown.unsubscribed_excluded}</span></div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Content blocks */}
          {!readOnly && (
            <div className="border border-white/10 bg-[#0A0A0A] p-4" data-testid="blocks-panel">
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                  <Layers className="w-4 h-4 text-[#00D4FF]" />
                  <div className="text-sm font-semibold text-white" data-testid="blocks-panel-title">
                    {(PANEL_STRINGS[blockLang] || PANEL_STRINGS.en).title}
                  </div>
                </div>
                {/* Feb-2026 — Compact language selector. Toggles the
                    language of NEWLY inserted blocks; existing body
                    content is not touched. */}
                <div className="flex text-[10px] uppercase tracking-widest border border-white/10" data-testid="blocks-lang-toggle">
                  <button
                    type="button"
                    onClick={() => setBlockLang('en')}
                    className={`px-2 py-0.5 ${blockLang === 'en' ? 'bg-[#00D4FF] text-black font-bold' : 'text-white/60 hover:text-white'}`}
                    data-testid="blocks-lang-en"
                  >
                    EN
                  </button>
                  <button
                    type="button"
                    onClick={() => setBlockLang('es')}
                    className={`px-2 py-0.5 ${blockLang === 'es' ? 'bg-[#00D4FF] text-black font-bold' : 'text-white/60 hover:text-white'}`}
                    data-testid="blocks-lang-es"
                  >
                    ES
                  </button>
                </div>
              </div>
              <div className="text-[11px] text-white/40 mb-3" data-testid="blocks-panel-subtitle">
                {(PANEL_STRINGS[blockLang] || PANEL_STRINGS.en).subtitle}
              </div>
              <div className="grid grid-cols-1 gap-2">
                {blocks.map((b) => (
                  <BlockButton
                    key={b.key}
                    block={{
                      ...b,
                      name: BLOCK_LABELS[blockLang]?.[b.key] || b.name,
                      hint: BLOCK_HINTS[blockLang]?.[b.key] || b.hint,
                    }}
                    onPick={insertBlock}
                    testid={`block-pick-${b.key}`}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Preview Modal — Feb-2026: real desktop-width preview
          (min(960px, 95vw) × 90vh) so admins see the email at
          production dimensions instead of a tiny thumbnail. Body
          content is 680px wide (the customer email container). */}
      {previewOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
          onClick={() => setPreviewOpen(false)}
          data-testid="preview-modal"
        >
          <div
            className="relative bg-[#0A0A0A] flex flex-col shadow-2xl mt-[2vh]"
            style={{ width: 'min(960px, 95vw)', height: '90vh' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close bar — sticky at top while scrolling */}
            <div className="flex items-center justify-between px-4 py-2 bg-[#0A0A0A] text-white border-b border-white/10 flex-shrink-0" style={{ height: 40 }}>
              <div className="text-sm font-semibold">Email Preview</div>
              <button onClick={() => setPreviewOpen(false)} className="text-white/60 hover:text-white" data-testid="close-preview">
                <X className="w-5 h-5" />
              </button>
            </div>
            {/* Centering wrapper — email container is 680px wide */}
            <div
              className="flex-1 overflow-y-auto flex justify-center bg-black"
              style={{ height: 'calc(90vh - 70px)' }}
            >
              <iframe
                title="Email Preview"
                srcDoc={previewHtml}
                className="bg-white"
                style={{ width: '680px', height: '100%', border: 0, overflowY: 'auto' }}
                data-testid="preview-iframe"
              />
            </div>
          </div>
        </div>
      )}
      {/* Save-as-Template modal (Phase 1 Campaign Templates) */}
      {saveTplOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !savingTpl && setSaveTplOpen(false)}
          data-testid="save-template-modal"
        >
          <div className="relative bg-[#0A0A0A] border border-white/10 w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="text-sm font-semibold text-white">Save as Template</div>
              <button onClick={() => setSaveTplOpen(false)} className="text-white/40 hover:text-white" data-testid="close-save-template">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] font-semibold tracking-widest uppercase text-white/55 mb-1 block">Template name</label>
                <input
                  type="text"
                  value={saveTplName}
                  onChange={(e) => setSaveTplName(e.target.value)}
                  placeholder="e.g. October Weekly Recap"
                  className="w-full px-3 py-2 bg-[#050505] border border-white/10 text-white text-sm focus:border-[#00D4FF] outline-none"
                  data-testid="save-template-name"
                  autoFocus
                />
              </div>
              <div>
                <label className="text-[10px] font-semibold tracking-widest uppercase text-white/55 mb-1 block">Category</label>
                <select
                  value={saveTplCategory}
                  onChange={(e) => setSaveTplCategory(e.target.value)}
                  className="w-full px-3 py-2 bg-[#050505] border border-white/10 text-white text-sm focus:border-[#00D4FF] outline-none"
                  data-testid="save-template-category"
                >
                  {TEMPLATE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="text-[11px] text-white/40">
                Captures the current subject, preheader and email body. Audience filters and sender profile are NOT saved.
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setSaveTplOpen(false)} disabled={savingTpl} className="btn-secondary text-sm disabled:opacity-50" data-testid="save-template-cancel">
                Cancel
              </button>
              <button
                onClick={submitSaveTemplate}
                disabled={savingTpl || !saveTplName.trim()}
                className="btn-primary inline-flex items-center gap-2 text-sm disabled:opacity-50"
                data-testid="save-template-submit"
              >
                {savingTpl ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bookmark className="w-4 h-4" />}
                Save Template
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Phase 3 — Recipient Preview modal (mandatory gate before Send for
          customers/suppliers audiences). */}
      <RecipientPreviewModal
        open={previewOpenP3}
        loading={previewLoading}
        preview={previewData}
        audienceType={audienceType}
        onClose={() => setPreviewOpenP3(false)}
        onConfirm={confirmPreview}
        onRefresh={openRecipientPreview}
      />

      {/* Feb-2026 — Visual Block config dialog (Hero, Product Spotlight,
          Custom CTA, Divider). One compact dialog whose form adapts to
          the picked block. */}
      {blockConfigOpen && (
        <BlockConfigDialog
          blockKey={blockConfigKey}
          params={blockConfigParams}
          setParams={setBlockConfigParams}
          onClose={() => setBlockConfigOpen(false)}
          onSubmit={submitBlockConfig}
          lang={blockLang}
        />
      )}
    </div>
  );
};

// ─── Helpers for the Phase-3 filter UI ──────────────────────────────
const CustSel = ({ label, value, options = [], onChange, testid }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">{label}</div>
    <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-2 py-1.5 bg-black border border-white/10 text-white text-xs" data-testid={testid}>
      <option value="">Any</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

// Feb-2026 — Multi-select country checklist for the Prospect Filters
// panel. Deliberately styled to match the existing `CustSel` slot so
// the rest of the campaign builder layout stays byte-identical. "Any"
// is the exclusive default and appears at the top of the list; picking
// any country auto-clears "Any", and unchecking every country flips
// back to "Any". Selections are OR-matched by the backend.
const CountryChecklist = ({ label, values = [], options = [], onChange, testid }) => {
  const [open, setOpen] = React.useState(false);
  const anySelected = !values || values.length === 0;
  const summary = anySelected
    ? 'Any'
    : values.length === 1
      ? values[0]
      : `${values.length} countries`;

  const toggle = (country) => {
    const has = values.includes(country);
    const next = has ? values.filter((c) => c !== country) : [...values, country];
    onChange(next);
  };
  const chooseAny = () => onChange([]);

  return (
    <div className="relative">
      <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">{label}</div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-2 py-1.5 bg-black border border-white/10 text-white text-xs text-left flex items-center justify-between"
        data-testid={testid}
      >
        <span className={anySelected ? 'text-white/60' : 'text-white'}>{summary}</span>
        <span className="text-white/40 text-[10px]">▾</span>
      </button>
      {open && (
        <>
          {/* Click-away layer */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto bg-[#050505] border border-white/15 shadow-lg"
            data-testid={`${testid}-menu`}
          >
            <label
              className="flex items-center gap-2 px-2 py-1.5 hover:bg-white/5 cursor-pointer border-b border-white/10"
              data-testid={`${testid}-option-any`}
            >
              <input
                type="checkbox"
                checked={anySelected}
                onChange={chooseAny}
                className="accent-[#00D4FF]"
              />
              <span className="text-xs text-white/80">Any</span>
            </label>
            {options.map((c) => (
              <label
                key={c}
                className="flex items-center gap-2 px-2 py-1.5 hover:bg-white/5 cursor-pointer"
                data-testid={`${testid}-option-${c}`}
              >
                <input
                  type="checkbox"
                  checked={values.includes(c)}
                  onChange={() => toggle(c)}
                  className="accent-[#00D4FF]"
                />
                <span className="text-xs text-white/80">{c}</span>
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const CustInput = ({ label, value, onChange, testid, placeholder }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">{label}</div>
    <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full px-2 py-1.5 bg-black border border-white/10 text-white text-xs" data-testid={testid} />
  </div>
);

const DateOpRow = ({ label, value, onChange, testid }) => {
  const op = value?.operator || '';
  return (
    <div className="mt-2">
      <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">{label}</div>
      <div className="flex flex-wrap gap-1.5 items-center">
        <select value={op} onChange={(e) => onChange({ operator: e.target.value, value: '', end_value: '' })} className="px-2 py-1.5 bg-black border border-white/10 text-white text-xs" data-testid={`${testid}-operator`}>
          <option value="">Any time</option>
          <option value="today">Today</option>
          <option value="yesterday">Yesterday</option>
          <option value="last_7_days">Last 7 Days</option>
          <option value="last_30_days">Last 30 Days</option>
          <option value="is">Is</option>
          <option value="before">Before</option>
          <option value="after">After</option>
          <option value="between">Between</option>
        </select>
        {['is', 'before', 'after', 'between'].includes(op) && (
          <input type="date" value={value?.value || ''} onChange={(e) => onChange({ ...value, value: e.target.value })} className="px-2 py-1.5 bg-black border border-white/10 text-white text-xs" data-testid={`${testid}-value`} />
        )}
        {op === 'between' && (
          <>
            <span className="text-white/30 text-xs">to</span>
            <input type="date" value={value?.end_value || ''} onChange={(e) => onChange({ ...value, end_value: e.target.value })} className="px-2 py-1.5 bg-black border border-white/10 text-white text-xs" data-testid={`${testid}-end-value`} />
          </>
        )}
      </div>
    </div>
  );
};

// ─── Feb-2026 — Visual Block Config Dialog ─────────────────────────
// Single compact dialog whose form fields adapt to the picked block
// key. Keeps params in the parent state so we can round-trip them
// straight to renderBlock() on submit.
const BLOCK_DIALOG_TITLES = {
  en: {
    hero: 'Configure Hero Block',
    product_spotlight: 'Configure Product Spotlight',
    custom_cta: 'Configure Custom CTA Button',
    divider: 'Configure Divider / Spacer',
  },
  es: {
    hero: 'Configurar Bloque Héroe',
    product_spotlight: 'Configurar Ficha de Producto',
    custom_cta: 'Configurar Botón CTA Personalizado',
    divider: 'Configurar Divisor / Espaciador',
  },
};

const BlockConfigDialog = ({ blockKey, params, setParams, onClose, onSubmit, lang }) => {
  const title = (BLOCK_DIALOG_TITLES[lang] || BLOCK_DIALOG_TITLES.en)[blockKey] || 'Configure Block';
  const update = (patch) => setParams({ ...params, ...patch });
  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="block-config-modal"
    >
      <div
        className="relative bg-[#0A0A0A] border border-white/10 w-full max-w-lg p-5 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid={`block-config-${blockKey}`}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm font-semibold text-white">{title}</div>
          <button onClick={onClose} className="text-white/40 hover:text-white" data-testid="block-config-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {blockKey === 'hero' && (
          <div className="space-y-3">
            <BCField label="Headline" value={params.headline || ''} onChange={(v) => update({ headline: v })} testid="bc-hero-headline" />
            <BCTextarea label="Description" value={params.description || ''} onChange={(v) => update({ description: v })} testid="bc-hero-description" />
            <BCField label="Image URL (optional)" value={params.image_url || ''} onChange={(v) => update({ image_url: v })} testid="bc-hero-image" placeholder="https://…" />
            <div className="grid grid-cols-2 gap-3">
              <BCField label="CTA Text" value={params.cta_text || ''} onChange={(v) => update({ cta_text: v })} testid="bc-hero-cta-text" />
              <BCField label="CTA URL" value={params.cta_url || ''} onChange={(v) => update({ cta_url: v })} testid="bc-hero-cta-url" />
            </div>
          </div>
        )}

        {blockKey === 'product_spotlight' && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <BCField label="Model" value={params.model || ''} onChange={(v) => update({ model: v })} testid="bc-ps-model" />
              <BCField label="Product ID" value={params.product_id || ''} onChange={(v) => update({ product_id: v })} testid="bc-ps-product-id" placeholder="LUX-…" />
            </div>
            <BCField label="Image URL (optional)" value={params.image_url || ''} onChange={(v) => update({ image_url: v })} testid="bc-ps-image" placeholder="https://…" />
            <BCTextarea label="Specifications" value={params.specs || ''} onChange={(v) => update({ specs: v })} testid="bc-ps-specs" placeholder="i5-1135G7 • 16GB • 256GB SSD" />
            <div className="grid grid-cols-3 gap-3">
              <BCSelect label="Grade" value={params.grade || 'A'} options={['A+', 'A', 'B', 'C']} onChange={(v) => update({ grade: v })} testid="bc-ps-grade" />
              <BCField label="Quantity" value={params.quantity || ''} onChange={(v) => update({ quantity: v })} testid="bc-ps-qty" placeholder="e.g. 120" />
              <BCField label="Customer Price" value={params.price || ''} onChange={(v) => update({ price: v })} testid="bc-ps-price" placeholder="$219.00" />
            </div>
            <div className="text-[10px] text-white/40 -mt-1">
              Grade A / A+ shows the price. Grade B / C automatically shows &quot;Request Quote&quot; regardless of what is entered.
            </div>
            <div className="grid grid-cols-2 gap-3">
              <BCField label="CTA Text" value={params.cta_text || ''} onChange={(v) => update({ cta_text: v })} testid="bc-ps-cta-text" />
              <BCField label="CTA URL" value={params.cta_url || ''} onChange={(v) => update({ cta_url: v })} testid="bc-ps-cta-url" />
            </div>
          </div>
        )}

        {blockKey === 'custom_cta' && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <BCField label="Button Text" value={params.cta_text || ''} onChange={(v) => update({ cta_text: v })} testid="bc-cta-text" />
              <BCField label="Button URL" value={params.cta_url || ''} onChange={(v) => update({ cta_url: v })} testid="bc-cta-url" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <BCSelect label="Color" value={params.color || 'cyan'} options={['cyan', 'amber', 'emerald', 'pink']} onChange={(v) => update({ color: v })} testid="bc-cta-color" />
              <BCSelect label="Alignment" value={params.align || 'left'} options={['left', 'center']} onChange={(v) => update({ align: v })} testid="bc-cta-align" />
            </div>
          </div>
        )}

        {blockKey === 'divider' && (
          <div className="space-y-3">
            <BCSelect label="Type" value={params.style || 'divider'} options={['divider', 'small', 'medium', 'large']} onChange={(v) => update({ style: v })} testid="bc-divider-style" />
            <div className="text-[10px] text-white/40">
              &quot;divider&quot; = thin line. small/medium/large = 12/24/48px vertical spacer.
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="btn-secondary text-sm" data-testid="block-config-cancel">Cancel</button>
          <button onClick={onSubmit} className="btn-primary text-sm" data-testid="block-config-submit">Insert Block</button>
        </div>
      </div>
    </div>
  );
};

const BCField = ({ label, value, onChange, testid, placeholder }) => (
  <div>
    <label className="text-[10px] font-semibold tracking-widest uppercase text-white/55 mb-1 block">{label}</label>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-2 bg-[#050505] border border-white/10 text-white text-sm focus:border-[#00D4FF] outline-none"
      data-testid={testid}
    />
  </div>
);

const BCTextarea = ({ label, value, onChange, testid, placeholder }) => (
  <div>
    <label className="text-[10px] font-semibold tracking-widest uppercase text-white/55 mb-1 block">{label}</label>
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={2}
      className="w-full px-3 py-2 bg-[#050505] border border-white/10 text-white text-sm focus:border-[#00D4FF] outline-none resize-y"
      data-testid={testid}
    />
  </div>
);

const BCSelect = ({ label, value, options, onChange, testid }) => (
  <div>
    <label className="text-[10px] font-semibold tracking-widest uppercase text-white/55 mb-1 block">{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 bg-[#050505] border border-white/10 text-white text-sm focus:border-[#00D4FF] outline-none"
      data-testid={testid}
    >
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);
